/**
 * IssueProgressContextStrip
 * ─────────────────────────
 * One-line situational awareness rendered ABOVE the PushProgressTracker
 * inside Push-Mode modals (primarily Resolve Issue) so the operator
 * answers, at a glance:
 *
 *   • Where is this job in its lifecycle right now?
 *   • How long has it been parked there?
 *   • Which downstream lifecycle stages does THIS issue block?
 *
 * Pure presentational — derives entirely from the Job + Issue passed in.
 * No mutations, no side effects.
 */
import { useMemo } from "react";
import { MapPin, Clock, Ban } from "lucide-react";
import type { Job, Issue } from "@/data/types";
import { cn } from "@/lib/utils";

interface Props {
  job: Job;
  issue?: Issue;
  className?: string;
}

/** Tracker stage keys (must match PushProgressTracker). */
type StageKey =
  | "review"
  | "permit"
  | "materials"
  | "crew"
  | "schedule"
  | "qa"
  | "closeout"
  | "invoice";

const STAGE_LABEL: Record<StageKey, string> = {
  review: "Review",
  permit: "Permit",
  materials: "Materials",
  crew: "Crew",
  schedule: "Schedule",
  qa: "QA",
  closeout: "Closeout",
  invoice: "Invoice",
};

/**
 * Map an issue to the lifecycle stages it most likely blocks.
 * Conservative — return only stages a reasonable operator would
 * agree are gated by the specific issue category.
 */
function getBlockedStages(issue?: Issue): StageKey[] {
  if (!issue) return [];

  const blocked = new Set<StageKey>();
  const desc = (issue.description ?? "").toLowerCase();
  const cat = issue.blockerCategory;
  const area = issue.affectedArea;
  const type = issue.issueType;

  // Material / sourcing
  if (
    type === "Material Issue" ||
    area === "Materials" ||
    cat === "Missing Material" ||
    cat === "Damaged Material" ||
    cat === "Supplier Delay" ||
    /discontinued|sourc|backorder|out of stock/i.test(desc)
  ) {
    blocked.add("materials");
    blocked.add("schedule");
  }

  // Permit
  if (
    type === "Permit Issue" ||
    area === "Permit" ||
    cat === "Permit Rejection" ||
    /permit/i.test(desc)
  ) {
    blocked.add("permit");
    blocked.add("schedule");
  }

  // Scheduling / crew
  if (
    type === "Scheduling Issue" ||
    area === "Scheduling" ||
    cat === "Scheduling Conflict" ||
    /reschedul|conflict|crew unavail|no.show/i.test(desc)
  ) {
    blocked.add("schedule");
  }
  if (type === "Contractor Issue" || /crew|contractor|installer/i.test(desc)) {
    blocked.add("crew");
    blocked.add("schedule");
  }

  // Site / access
  if (/access|gate|lockbox|on.?site|home.?owner unavail/i.test(desc)) {
    blocked.add("schedule");
  }

  // QA / closeout / invoice signals
  if (cat === "Inspection Failure" || /punchlist|qa fail|defect|inspection/i.test(desc)) {
    blocked.add("qa");
    blocked.add("closeout");
    blocked.add("invoice");
  }

  // Financial signals
  if (/payment|deposit|invoice|funding|finance/i.test(desc)) {
    blocked.add("invoice");
  }

  return Array.from(blocked);
}

/**
 * Best-effort "days in current stage" using the most recent stage-transition
 * activity entry; falls back to the job's most recent activity timestamp.
 */
function getDaysInStage(job: Job): number | null {
  const history = job.activityHistory ?? [];
  if (history.length === 0) return null;
  const stageEntry = [...history]
    .reverse()
    .find((h) => /stage|moved to|transition|advanced/i.test(h.action));
  const fallback = history[history.length - 1];
  const ref = stageEntry ?? fallback;
  if (!ref?.date) return null;
  const t = new Date(ref.date).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

export function IssueProgressContextStrip({ job, issue, className }: Props) {
  const blocked = useMemo(() => getBlockedStages(issue), [issue]);
  const days = useMemo(() => getDaysInStage(job), [job]);

  const stale = days !== null && days >= 5;
  const warm = days !== null && days >= 3 && days < 5;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px]",
        className,
      )}
    >
      <div className="flex items-center gap-1.5 min-w-0">
        <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Stage</span>
        <span className="font-semibold text-foreground truncate">{job.currentStage}</span>
      </div>

      <div className="flex items-center gap-1.5 min-w-0">
        <Clock
          className={cn(
            "h-3.5 w-3.5 shrink-0",
            stale && "text-destructive",
            warm && "text-warning",
            !stale && !warm && "text-muted-foreground",
          )}
        />
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">In stage</span>
        <span
          className={cn(
            "font-semibold",
            stale && "text-destructive",
            warm && "text-warning",
            !stale && !warm && "text-foreground",
          )}
        >
          {days === null ? "—" : days === 0 ? "Today" : `${days}d`}
        </span>
      </div>

      {issue && (
        <div className="flex items-center gap-1.5 min-w-0">
          <Ban
            className={cn(
              "h-3.5 w-3.5 shrink-0",
              blocked.length > 0 ? "text-destructive" : "text-muted-foreground",
            )}
          />
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Blocking</span>
          <span
            className={cn(
              "font-semibold truncate",
              blocked.length > 0 ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {blocked.length === 0
              ? "Nothing downstream"
              : blocked.map((k) => STAGE_LABEL[k]).join(" · ")}
          </span>
        </div>
      )}
    </div>
  );
}
